import { useState } from "react";
import { X, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";
import { motion } from "framer-motion";

const API_URL = "https://ffmqw31y7k.execute-api.us-east-1.amazonaws.com/pdf";
const API_KEY = "154122054";

const categories = [
    "STEEL STRUCTURE",
    "SEALENTS",
    "RTV",
    "ADHESIVES",
    "ROOF COATINGS",
    "AKT AUTOMOTIVE"
];

const EditPdf = ({ pdf, onClose }) => {
    const [title, setTitle] = useState(pdf?.title || "");
    const [category, setCategory] = useState(pdf?.category || "");
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpdate = async () => {
        if (!title || !category) {
            setError("* Title and category are required!");
            setTimeout(() => setError(""), 2000); // Hide error after 2 seconds
            return;
        }

        setLoading(true);

        let fileContent = null;
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            fileContent = await new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result.split(",")[1]);
            });
        }

        try {
            const response = await fetch(`${API_URL}/${pdf.id}`, {
                method: "PUT",
                headers: {
                    "x-api-key": API_KEY,
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    title,
                    category,
                    fileContent: fileContent || undefined,
                    userId: pdf.userId
                })
            });

            if (!response.ok) throw new Error("Update failed");

            setTimeout(() => {
                setLoading(false);
                setSuccess(true);

                setTimeout(() => {
                    setSuccess(false);
                    onClose(); // Close popup after success
                    window.location.reload();
                }, 3000); // Show success message for 3 seconds
            }, 2000); // Show animation for 2 seconds

        } catch (error) {
            toast.error(error.message, { duration: 3000 });
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
                {/* Close Button */}
                <button onClick={onClose} className="absolute top-2 right-2 text-gray-600 hover:text-red-600">
                    <X size={24} />
                </button>

                <motion.h2
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="text-2xl font-bold text-gray-900 bg-clip-text text-center mb-4 text-transparent bg-gradient-to-r from-blue-500 to-purple-500"
                >
                    Edit PDF
                </motion.h2>

                {/* Error Message */}
                {error && <div className="text-red-600 text-sm mb-2 text-center">{error}</div>}

                {loading ? (
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                        <p className="text-lg font-medium text-blue-500">Updating...</p>
                    </div>
                ) : success ? (
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <CheckCircle size={40} className="text-green-500" />
                        <p className="text-lg font-medium text-green-500">PDF updated successfully!</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <label className="text-gray-600 font-medium">PDF Name :</label>
                        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none mt-1"
                        />

                        <label className="text-gray-600 font-medium">Select Page :</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)}
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none mt-1">
                            <option value="">Select a category</option>
                            {categories.map((cat, index) => <option key={index} value={cat}>{cat}</option>)}
                        </select>

                        <label className="text-gray-600 font-medium">Upload New PDF (Optional)</label>
                        <input type="file" accept="application/pdf" onChange={handleFileChange}
                            className="w-full p-2 border rounded-lg bg-gray-100 mt-1" />

                        <div className="flex justify-between mt-4">
                            {/* Update Button (Hidden while loading) */}
                            {!loading && !success && (
                                <button onClick={handleUpdate}
                                    className="flex items-center gap-2 bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition">
                                    <CheckCircle size={20} /> Update
                                </button>
                            )}

                            {/* Cancel Button */}
                            <button onClick={onClose}
                                className="flex items-center gap-2 bg-gray-400 text-white p-3 rounded-lg hover:bg-gray-500 transition">
                                <X size={20} /> Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditPdf;
